import { useEffect, useMemo, useState } from 'react';
import { CoversheetTemplate } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/messages.pb';
import dayjs from 'dayjs';
import { FaxQueries } from '@frontend/api-fax';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { FaxMediaHooks, FaxMediaQueries } from '@frontend/api-fax-media';
import { getUser } from '@frontend/auth-helpers';
import { DeviceSize } from '@frontend/device-size-field';
import { EditableText } from '@frontend/editable-text';
import { HttpError } from '@frontend/fetch';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  ConfirmationModal,
  ListRow,
  PopoverMenu,
  SpinningLoader,
  Text,
  useAlert,
  useDebouncedValue,
  useFormField,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';
import { AssignLocationAccessModal } from './assign-location-access-modal';
import { TemplatePreviewModal } from './template-preview-modal';

type Props = {
  template: CoversheetTemplate;
  locationId: string;
  contactId?: string;
  previousDraftId?: string;
  isTemplateDataLoading?: boolean;
  disableTemplates?: boolean;
  trackingId?: string;
};

export const TemplateCard = ({
  template,
  locationId,
  contactId,
  previousDraftId,
  isTemplateDataLoading,
  disableTemplates,
  trackingId,
}: Props) => {
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const alert = useAlert();
  const user = getUser();
  const { selectedLocationIds } = useAppScopeStore();
  const invalidateFaxTemplateList = FaxMediaHooks.useInvalidateFaxTemplatesList();
  const { modalProps: previewModalProps, triggerProps: previewTriggerProps } = useModalControl();
  const { modalProps: deleteModalProps, openModal: openDeleteModal } = useModalControl();
  const { modalProps: locationAccessModalProps, triggerProps: locationAccessTriggerProps } = useModalControl();
  const [isCreateFaxWithTemplateLoading, setIsCreateFaxWithTemplateLoading] = useState(false);

  const { data: faxNumber } = FaxQueries.useQueryDefaultFaxNumber(locationId);
  const invalidateCoversheetTemplate = FaxMediaQueries.useInvalidateCoversheetTemplate();
  const { mutateAsync: removeCoversheetFile } = FaxDraftQueries.useUnsetCoversheetUploadedFile({
    onSuccess: (res) => {
      invalidateDraft(res.id);
      invalidateCoversheetTemplate(res.id, locationId);
    },
  });
  const { mutateAsync: removeCoversheetHtml } = FaxDraftQueries.useUnsetCoversheetHtml({
    onSuccess: (res) => {
      invalidateDraft(res.id);
      invalidateCoversheetTemplate(res.id, locationId);
    },
  });
  const { mutateAsync: updateCoversheetFileWithTemplate } = FaxDraftQueries.useUpdateCoversheetHtmlFromTemplate({
    onSuccess: (res) => {
      invalidateDraft(res.id);
      invalidateCoversheetTemplate(res.id, locationId);
    },
  });
  const invalidateDraftList = FaxDraftQueries.useInvalidateDraftList();
  const invalidateDraft = FaxDraftQueries.useInvalidateDraftById();

  const { mutateAsync: createDraft } = FaxDraftQueries.useStartDraft({
    onSuccess: () => {
      invalidateDraftList();
    },
    onError: (err) => {
      const error = err as HttpError;
      if (error.message.toLowerCase().includes('invalid fax number')) {
        alert.error(t('Invalid fax number. Please reach out to support to set this up.'));
      } else {
        alert.error(t('An unknown error occurred'));
      }
    },
  });

  const { mutateAsync: addRecipient } = FaxDraftQueries.useAddRecipient({});
  const { mutateAsync: setTemplate } = FaxDraftQueries.useUpdateCoversheetHtmlFromTemplate({});

  const { mutateAsync: editCoversheetTemplate, isLoading: isEditing } = FaxMediaQueries.useUpsertCoversheetTemplate({
    onSuccess: () => {
      invalidateFaxTemplateList();
      alert.success({
        message: t('Template edited successfully.'),
      });
    },
    onError: () => {
      alert.error({
        message: t('Error editing template.'),
      });
    },
  });

  const { mutateAsync: duplicateCoversheetTemplate, isLoading: isDuplicating } =
    FaxMediaQueries.useUpsertCoversheetTemplate({
      onSuccess: () => {
        invalidateFaxTemplateList();
        alert.success({
          message: t('Template duplicated successfully.'),
        });
      },
      onError: () => {
        alert.error({
          message: t('Error duplicating template.'),
        });
      },
    });

  const { mutateAsync: deleteCoversheetTemplate, isLoading: isLoadingDeleteTemplate } =
    FaxMediaQueries.useDeleteCoversheetTemplate({
      onSuccess: () => {
        invalidateFaxTemplateList();
        alert.success({
          message: t('Template deleted successfully.'),
        });
      },
      onError: () => {
        alert.error({
          message: t('Error deleting template.'),
        });
      },
    });

  const deviceSizeProps = useFormField({ type: 'optionswitch', value: 'desktop' }) as BaseFieldProps<DeviceSize>;
  const titleFieldProps = useFormField({ type: 'text', value: template.name ?? '', placeholder: t('Enter a title') });
  const debouncedValue = useDebouncedValue(titleFieldProps.value, 1000);

  const formattedDate = dayjs(template.updatedAt).format('MMMM DD, YYYY');
  const currentTime = new Date().toISOString();

  const { data: smallThumbnail } = FaxDraftQueries.useQueryFaxMedia(
    template.attributes.smallThumbnail ?? '',
    template.creatorLocationId ?? '',
    {
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
      keepPreviousData: false,
      enabled: !!template.attributes.smallThumbnail && !!template.creatorLocationId,
    }
  );

  const showMissingPhoneMessage = () => {
    alert.error(
      t('Your location does not have a fax number to send from. Please reach out to support to set this up.')
    );
  };

  const createNewFaxWithTemplate = async () => {
    if (!faxNumber) {
      return showMissingPhoneMessage();
    }
    try {
      setIsCreateFaxWithTemplateLoading(true);
      let draftId = previousDraftId ?? '';
      if (draftId) {
        await removeCoversheetHtml({ draftId, locationId });
        await removeCoversheetFile({ draftId, locationId });
        if (template?.id)
          updateCoversheetFileWithTemplate({
            draftId,
            locationId,
            templateId: template?.id,
          });
      } else {
        const draftRes = await createDraft({ faxPhone: faxNumber, locationId });
        draftId = draftRes.id;
      }

      if (contactId) {
        addRecipient({ draftId, locationId, contactId });
      }
      const templateRes = await setTemplate({
        draftId,
        templateId: template.id ?? '',
        locationId: locationId,
      });
      navigate({
        to: '/fax/drafts/$draftId/cover',
        params: { draftId: templateRes.id },
        search: { selectedLocationId: locationId },
      });
    } catch (_err) {
      alert.error(t('Error creating fax'));
    } finally {
      setIsCreateFaxWithTemplateLoading(false);
    }
  };

  const src = useMemo(() => {
    return smallThumbnail ? URL.createObjectURL(smallThumbnail) : undefined;
  }, [JSON.stringify(smallThumbnail)]);

  const { getMenuProps, getTriggerProps, close } = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: { offset: 0 },
  });

  const actions = [
    {
      Icon: <Icon name='view' size={20} color='subdued' />,
      label: t('View'),
      onClick: () => {
        close();
        if (!template.html) {
          alert.error(t('Template preview is not available.'));
          return;
        }
        previewTriggerProps.onClick();
      },
    },
    {
      Icon: <Icon name='edit' size={20} color='subdued' />,
      label: t('Edit'),
      onClick: async () => {
        close();
        navigate({
          to: '/fax/drafts/template-editor',
          search: {
            selectedLocationId: locationId,
            templateId: template.id,
          },
        });
      },
    },
    {
      Icon: <Icon name='arrow-right-small' size={20} color='subdued' />,
      label: t('Select This Template'),
      onClick: createNewFaxWithTemplate,
    },
    {
      Icon: <Icon name='copy' size={20} color='subdued' />,
      label: t('Duplicate'),
      onClick: async () => {
        close();
        await duplicateCoversheetTemplate({
          locationId,
          coversheetTemplate: {
            name: `Copy of ${template.name}`,
            locationIds: template.locationIds,
            html: template.html,
            createdAt: currentTime,
            updatedAt: currentTime,
            userId: user?.userID || '',
            attributes: template.attributes,
          },
        });
      },
    },
    ...(selectedLocationIds.length > 1
      ? [
          {
            Icon: <Icon name='location-small' size={20} color='subdued' />,
            label: t('Location Access'),
            onClick: () => {
              close();
              locationAccessTriggerProps.onClick();
            },
          },
        ]
      : []),
    {
      Icon: <Icon name='trash' size={20} color='error' />,
      label: t('Delete Template'),
      onClick: () => {
        close();
        openDeleteModal();
      },
    },
  ];

  const handleUpdateTemplateName = async () => {
    if (!template || !template.id) return;
    await editCoversheetTemplate({
      locationId,
      coversheetTemplate: {
        id: template.id,
        name: debouncedValue,
        html: template.html,
        creatorLocationId: template.creatorLocationId,
        createdAt: template.createdAt,
        locationIds: template.locationIds,
        updatedAt: currentTime,
        userId: user?.userID || '',
        attributes: template.attributes,
      },
    });
  };

  const handleUpdateLocationAccess = (locationIds: string[]) => {
    return editCoversheetTemplate({
      locationId,
      coversheetTemplate: {
        id: template.id,
        name: template.name,
        html: template.html,
        createdAt: template.createdAt,
        creatorLocationId: template.creatorLocationId,
        locationIds: locationIds.length ? locationIds : template.locationIds,
        updatedAt: currentTime,
        userId: user?.userID || '',
        attributes: template.attributes,
      },
    });
  };

  useEffect(() => {
    if (debouncedValue && debouncedValue !== template.name) {
      handleUpdateTemplateName();
    }
  }, [debouncedValue]);

  const disableTemplate =
    isCreateFaxWithTemplateLoading || isLoadingDeleteTemplate || isEditing || isDuplicating || disableTemplates;

  return (
    <>
      <div
        css={{
          border: `1px solid ${theme.colors.neutral20}`,
          borderRadius: theme.borderRadius.medium,
          position: 'relative',
          cursor: 'pointer',
          height: '265px',
          '&:hover': {
            border: `1px solid ${theme.colors.primary50}`,
          },
          overflow: 'hidden',
        }}
        data-trackingid={trackingId}
      >
        <div
          onClick={() => {
            if (!disableTemplate) createNewFaxWithTemplate();
          }}
          css={{
            background: `linear-gradient(180deg, ${theme.colors.neutral5} 0%, ${theme.colors.white} 60%)`,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            padding: theme.spacing(2),
            borderRadius: theme.spacing(0.25),
          }}
        >
          {isCreateFaxWithTemplateLoading && (
            <SpinningLoader size='medium' css={{ position: 'absolute', top: '25%', left: '42.5%' }} />
          )}
          <img
            src={src}
            css={{
              boxShadow: theme.shadows.light,
            }}
          />
        </div>
        <div
          css={{
            position: 'absolute',
            bottom: 0,
            background: 'white',
            padding: theme.spacing(2),
            borderTop: `1px solid ${theme.colors.neutral20}`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderBottomLeftRadius: theme.borderRadius.medium,
            borderBottomRightRadius: theme.borderRadius.medium,
            cursor: 'pointer',
          }}
        >
          <EditableText
            fieldProps={titleFieldProps}
            hideUntilHovered
            disabled={disableTemplate}
            css={{
              'span, input': {
                fontSize: theme.fontSize(16),
                height: 'auto',
                paddingLeft: 0,
              },
            }}
          />
          <div
            css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => {
              if (!disableTemplate) createNewFaxWithTemplate();
            }}
          >
            <Text color='subdued' size='small'>
              {t('Updated at {{formattedDate}}', { formattedDate })}
            </Text>
            <Button
              {...getTriggerProps()}
              iconName='more'
              variant='secondary'
              size='large'
              loading={isLoadingDeleteTemplate || isEditing || isDuplicating}
              disabled={disableTemplate}
            />
          </div>
        </div>
        <PopoverMenu {...getMenuProps()}>
          {actions.map(({ Icon, label, onClick }, index) => (
            <ListRow
              key={index}
              css={{
                border: 'unset',
                display: 'flex',
                columnGap: theme.spacing(1),
                padding: theme.spacing(1.25, 2),
                cursor: 'pointer',
                borderBottom: `1px solid ${theme.colors.neutral20}`,
                '&:first-of-type': {
                  paddingTop: theme.spacing(1),
                },
                '&:last-of-type': {
                  borderBottom: 'none',
                  paddingBottom: theme.spacing(1),
                },
              }}
              onClick={onClick}
            >
              {Icon}
              <Text color={index === 5 ? 'error' : 'default'}>{label}</Text>
            </ListRow>
          ))}
        </PopoverMenu>
      </div>
      {previewModalProps.show && (
        <TemplatePreviewModal
          deviceSizeProps={deviceSizeProps}
          modalProps={previewModalProps}
          previewHtml={template.html}
          createNewFaxWithTemplate={createNewFaxWithTemplate}
        />
      )}
      {selectedLocationIds.length > 1 && (
        <AssignLocationAccessModal
          locationsWithAccess={template.locationIds ?? []}
          isTemplateDataLoading={isTemplateDataLoading}
          modalProps={locationAccessModalProps}
          onSave={handleUpdateLocationAccess}
          mode='action'
        />
      )}
      <ConfirmationModal
        {...deleteModalProps}
        title={t('Are You Sure?')}
        message={
          <Trans t={t}>
            <Text as='span'>Are you sure you want to delete </Text>
            <Text as='span' weight='bold'>
              {template?.name ?? 'this template'}
            </Text>
            <Text as='span'>?</Text>
          </Trans>
        }
        confirmLabel={t('Delete Template')}
        onConfirm={() => {
          deleteCoversheetTemplate({
            id: template.id ?? '',
          });
        }}
        destructive={true}
      />
    </>
  );
};
